// stores/panelDataStore.lifecycle.ts — transient subscription side-effect bookkeeping.
//
// Extracted from panelDataStore.ts (to keep that file < 250 LOC) following the same pattern
// as panelDataStore.older.ts: maps/helpers that are pure side-effect bookkeeping — NOT store
// state — live in a sibling module. The store's subscribe/ensureLoaded/clear actions import
// these to track in-flight REST loads and the deferred-teardown grace window.

import type { SymbolLoadHandle } from '../api/panelDataFetcher';
import type { SymbolKey } from './panelDataStore.types';

/** Transient abort handles for in-flight REST seed loads — NOT store state (side-effect
 *  bookkeeping only). Cleared on resolve/teardown so clear(symbol) can cancel. */
export const inflight = new Map<SymbolKey, SymbolLoadHandle>();

/** Pending deferred teardowns. When refcount hits 0 we DON'T tear down synchronously —
 *  React StrictMode (and fast symbol switches) unmount→remount within the same tick, and
 *  a synchronous clear() would abort the in-flight REST seed started by the first mount,
 *  leaving the chart permanently empty. Instead we schedule teardown after a short grace
 *  window; a re-subscribe before it fires cancels it (the entry + in-flight load survive). */
export const pendingTeardown = new Map<SymbolKey, ReturnType<typeof setTimeout>>();

export const TEARDOWN_GRACE_MS = 250;

/** Cancel a scheduled deferred teardown for a symbol (a re-subscribe within the grace window). */
export function cancelPendingTeardown(symbol: SymbolKey): void {
  const t = pendingTeardown.get(symbol);
  if (t !== undefined) {
    clearTimeout(t);
    pendingTeardown.delete(symbol);
  }
}
